/**
 * Authentication Store
 * Manages authentication state using Zustand
 * 
 * Tokens are stored in cookies by plz-admin, we just read them here
 */

import { create } from 'zustand';
import type { AuthState, User } from '../types/auth.types';
import { getTokensFromCookies, extractUserFromToken } from '../lib/auth/cookies';

/**
 * Auth store actions
 */
interface AuthActions {
  /**
   * Sets authenticated state after reading from cookies
   */
  setAuthenticated: (token: string, refreshToken: string, expiresAt: number, user: User) => void;
  
  /**
   * Legacy login method - for compatibility
   * @deprecated Use setAuthenticated instead
   */
  login: (token: string, refreshToken: string, expiresAt: number, user: User) => Promise<void>;
  
  /**
   * Clears authentication state (doesn't clear cookies - that's plz-admin's job)
   */
  logout: () => void;
  
  /**
   * Updates user information
   */
  setUser: (user: User | null) => void;
  
  /**
   * Updates loading state
   */
  setLoading: (isLoading: boolean) => void;
  
  /**
   * Checks if the current token is expired
   */
  isTokenExpired: () => boolean;
  
  /**
   * Refreshes auth state from cookies
   * Useful when cookies might have been updated by another tab
   */
  refreshFromCookies: () => void;
}

/**
 * Extended auth state with expiresAt
 */
interface AuthStateExtended extends AuthState {
  expiresAt: number | null;
}

/**
 * Combined auth store type
 */
type AuthStore = AuthStateExtended & AuthActions;

/**
 * Create the auth store
 */
export const useAuthStore = create<AuthStore>()((set, get) => ({
  // Initial state
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true until we check cookies
  expiresAt: null,

  // Actions
  setAuthenticated: (token: string, refreshToken: string, expiresAt: number, user: User) => {
    set({
      user,
      token,
      refreshToken,
      isAuthenticated: true,
      isLoading: false,
      expiresAt,
    });
  },

  // Legacy method for compatibility
  login: async (token: string, refreshToken: string, expiresAt: number, user: User) => {
    set({
      user,
      token,
      refreshToken,
      isAuthenticated: true,
      isLoading: false,
      expiresAt,
    });
  },

  logout: () => {
    set({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      expiresAt: null,
    });
    
    // Redirect to plz-admin logout or landing page
    // The actual cookie clearing is handled by plz-admin
  },

  setUser: (user: User | null) => {
    set({ user });
  },

  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  isTokenExpired: (): boolean => {
    const state = get();
    if (!state.expiresAt) return true;
    
    const now = Date.now();
    const buffer = 30 * 1000; // 30 second buffer
    return state.expiresAt - buffer <= now;
  },

  refreshFromCookies: () => {
    const { sessionToken, refreshToken } = getTokensFromCookies();
    
    if (!sessionToken) {
      set({
        token: null,
        refreshToken: null,
        isAuthenticated: false,
      });
      return;
    }

    const tokenData = extractUserFromToken(sessionToken);
    
    set({
      token: sessionToken,
      refreshToken,
      expiresAt: tokenData?.expiresAt || null,
    });
  },
}));

/**
 * Selector hooks for common use cases
 */
export const useAuth = () => useAuthStore((state) => ({
  user: state.user,
  isAuthenticated: state.isAuthenticated,
  isLoading: state.isLoading,
}));

export const useAuthActions = () => {
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const setUser = useAuthStore((state) => state.setUser);
  const setLoading = useAuthStore((state) => state.setLoading);
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired);
  const refreshFromCookies = useAuthStore((state) => state.refreshFromCookies);

  return {
    setAuthenticated,
    login,
    logout,
    setUser,
    setLoading,
    isTokenExpired,
    refreshFromCookies,
  };
};
